import type { ApprovalStepDTO, ApprovalStepStatus } from '../approval.types';
import dayjs from 'dayjs';
import i18next from 'i18next';

export class ApprovalStepModel {
  private readonly _dto: ApprovalStepDTO;

  constructor(dto: ApprovalStepDTO) {
    this._dto = dto;
  }

  get originDTO(): ApprovalStepDTO {
    return this._dto;
  }

  get id(): string {
    return this._dto.id || '';
  }

  get tenantId(): string {
    return this._dto.tenantId || '';
  }

  get approverId(): string {
    return this._dto.approverId || '';
  }

  get approver() {
    return this._dto.approver;
  }

  get stepOrder(): number {
    return this._dto.stepOrder || 0;
  }

  get status(): ApprovalStepStatus {
    return this._dto.status;
  }

  get approvedAt(): string {
    return this._dto.approvedAt ? dayjs(this._dto.approvedAt).format('YYYY.MM.DD HH:mm') : '';
  }

  get comment(): string {
    return this._dto.comment ?? '';
  }

  get statusLabel(): string {
    switch (this.status) {
      case 'WAITING':
        return i18next.t('Approval.Step.Status.Waiting') || '대기';
      case 'APPROVED':
        return i18next.t('Approval.Step.Status.Approved') || '승인';
      case 'REJECTED':
        return i18next.t('Approval.Step.Status.Rejected') || '반려';
      case 'IN_PROGRESS':
        return i18next.t('Approval.Step.Status.InProgress') || '진행중';
      case 'SKIPPED':
        return i18next.t('Approval.Step.Status.Skipped') || '건너뛰기';
      default:
        return '';
    }
  }

  get statusBackgroundColor(): string {
    switch (this.status) {
      case 'WAITING':
        return 'bg-gray-dark';
      case 'APPROVED':
        return 'bg-green';
      case 'REJECTED':
        return 'bg-yellow';
      case 'IN_PROGRESS':
        return 'bg-pink';
      case 'SKIPPED':
        return 'bg-gray-dark';
      default:
        return 'bg-gray-dark';
    }
  }

  get statusTextColor(): string {
    switch (this.status) {
      case 'WAITING':
        return 'text-white';
      case 'APPROVED':
        return 'text-white';
      case 'REJECTED':
        return 'text-gray-sub-darker';
      case 'IN_PROGRESS':
        return 'text-white';
      case 'SKIPPED':
        return 'text-gray-sub-darker';
      default:
        return 'text-white';
    }
  }

  get approverName(): string {
    return this.approver?.name ?? '';
  }

  get approverEmail(): string {
    return this.approver?.email ?? '';
  }

  get approverUsergroups(): string {
    return this.approver?.userGroups ?? '';
  }

  get approverCustomRole(): string {
    return this.approver?.customRole ?? '';
  }

  get approverInfoLabel(): string {
    let approverInfo = '';
    if (this.approverUsergroups && this.approverCustomRole) {
      approverInfo = `(${this.approverUsergroups}/${this.approverCustomRole})`;
    } else if (this.approverUsergroups) {
      approverInfo = `(${this.approverUsergroups})`;
    }
    return approverInfo ? `${this.approverName} ${approverInfo}` : this.approverName;
  }

  get isCompleted(): boolean {
    return this.status === 'APPROVED' || this.status === 'REJECTED';
  }

  get updatedDate(): string {
    return this._dto.updatedDate ? dayjs(this._dto.updatedDate).format('YYYY.MM.DD, HH:mm:ss') : '';
  }
}
